// Formulaires du budget (lot L4-B) en fenêtres modales : création du budget d'un exercice, ligne
// budgétaire, acte sur l'état du budget (délibération et date exigées pour l'adoption, date de
// transmission pour le caractère exécutoire, motif pour un renvoi), décision modificative (mouvements
// positifs ou négatifs sur des lignes distinctes).
import { ACTIONS_BUDGET, CODES_SECTIONS_BUDGET, CODES_SENS_LIGNE, SECTIONS_BUDGET, SENS_LIGNE_BUDGET, type ActionBudget, type LigneBudgetaire, type SectionBudget, type SensLigneBudget } from '@cc/contrats';
import { Bandeau, Bouton, Champ, Dialogue, Selection, ZoneTexte } from '@cc/ui';
import { Plus, Trash2 } from 'lucide-react';
import { useState } from 'react';
import { useAction } from '../../commun/useAction';
import { creerBudget, creerLigne, deciderModification, INVALIDER_BUDGET, transiterBudget } from './api';
import { aujourdhuiIso, controlerMouvements, francs, type MouvementSaisi } from './logique';
import styles from './budget.module.css';

const actionsDialogue = (onFermer: () => void, libelle: string, enCours: boolean, lancer: () => void) => (
  <>
    <Bouton variante="secondaire" onClick={onFermer}>Annuler</Bouton>
    <Bouton variante="principal" chargement={enCours} onClick={lancer}>{libelle}</Bouton>
  </>
);

export function FormulaireBudget({ exercice, onFermer, onCree }: { exercice: number; onFermer: () => void; onCree: (id: string) => void }) {
  const [v, setV] = useState({ exercice: String(exercice), libelle: `Budget primitif ${exercice}` });
  const action = useAction({
    executer: () => creerBudget({ exercice: Number(v.exercice), libelle: v.libelle }),
    succes: (b) => `Budget ${b.exercice} créé en préparation.`,
    invalider: INVALIDER_BUDGET,
    apres: (b) => onCree(b.id),
  });
  const champ = (nom: string) => action.erreursChamps.find((e) => e.champ === nom)?.message;
  return (
    <Dialogue ouvert titre="Nouveau budget" description="Un budget par exercice ; ses lignes se saisissent ou s'importent en préparation." onFermer={onFermer} actions={actionsDialogue(onFermer, 'Créer le budget', action.enCours, () => action.lancer(undefined))}>
      <div className={styles.grille}>
        <Champ libelle="Exercice" type="number" value={v.exercice} onChange={(e) => setV((x) => ({ ...x, exercice: e.target.value }))} erreur={champ('exercice')} required />
        <Champ libelle="Libellé" value={v.libelle} onChange={(e) => setV((x) => ({ ...x, libelle: e.target.value }))} erreur={champ('libelle')} required />
      </div>
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

export function FormulaireLigne({ budgetId, ouverteApresAdoption, onFermer }: { budgetId: string; ouverteApresAdoption: boolean; onFermer: () => void }) {
  const [v, setV] = useState({ section: 'fonctionnement' as SectionBudget, sens: 'depense' as SensLigneBudget, code: '', chapitre: '', article: '', libelle: '', prevu: '0' });
  const action = useAction({
    executer: () => creerLigne(budgetId, { section: v.section, sens: v.sens, code: v.code.trim(), chapitre: v.chapitre || undefined, article: v.article || undefined, libelle: v.libelle, prevu: Number(v.prevu) }),
    succes: (l) => `Ligne ${l.code} ajoutée.`,
    invalider: INVALIDER_BUDGET,
    apres: onFermer,
  });
  const champ = (nom: string) => action.erreursChamps.find((e) => e.champ === nom)?.message;
  return (
    <Dialogue ouvert titre="Nouvelle ligne budgétaire" description={ouverteApresAdoption ? 'Budget adopté : la ligne s\'ouvre avec un prévu nul et reçoit ses crédits par décision modificative.' : 'Code de nomenclature libre (chapitre, article, paragraphe) ; montant prévu en francs CFA.'} onFermer={onFermer} actions={actionsDialogue(onFermer, 'Ajouter la ligne', action.enCours, () => action.lancer(undefined))}>
      <div className={styles.grille}>
        <Selection libelle="Section" options={CODES_SECTIONS_BUDGET.map((s) => ({ valeur: s, libelle: SECTIONS_BUDGET[s] }))} value={v.section} onChange={(e) => setV((x) => ({ ...x, section: e.target.value as SectionBudget }))} />
        <Selection libelle="Sens" options={CODES_SENS_LIGNE.map((s) => ({ valeur: s, libelle: SENS_LIGNE_BUDGET[s] }))} value={v.sens} onChange={(e) => setV((x) => ({ ...x, sens: e.target.value as SensLigneBudget }))} />
        <Champ libelle="Code" value={v.code} onChange={(e) => setV((x) => ({ ...x, code: e.target.value }))} erreur={champ('code')} required />
        <Champ libelle="Chapitre" value={v.chapitre} onChange={(e) => setV((x) => ({ ...x, chapitre: e.target.value }))} />
        <Champ libelle="Article" value={v.article} onChange={(e) => setV((x) => ({ ...x, article: e.target.value }))} />
        <Champ libelle="Prévu (F)" type="number" value={ouverteApresAdoption ? '0' : v.prevu} disabled={ouverteApresAdoption} onChange={(e) => setV((x) => ({ ...x, prevu: e.target.value }))} erreur={champ('prevu')} required />
      </div>
      <Champ libelle="Libellé" value={v.libelle} onChange={(e) => setV((x) => ({ ...x, libelle: e.target.value }))} erreur={champ('libelle')} required />
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

export function FormulaireTransition({ budgetId, action: code, onFermer }: { budgetId: string; action: ActionBudget; onFermer: () => void }) {
  const [v, setV] = useState({ deliberation_reference: '', date: aujourdhuiIso(), motif: '' });
  const def = ACTIONS_BUDGET[code];
  const action = useAction({
    executer: () => transiterBudget(budgetId, {
      action: code,
      deliberation_reference: code === 'adopter' ? v.deliberation_reference : undefined,
      date: code === 'adopter' || code === 'rendre_executoire' || code === 'clore' ? v.date : undefined,
      motif: v.motif || undefined,
    }),
    succes: `${def.libelle} : acte enregistré.`,
    invalider: INVALIDER_BUDGET,
    apres: onFermer,
  });
  const champ = (nom: string) => action.erreursChamps.find((e) => e.champ === nom)?.message;
  return (
    <Dialogue ouvert titre={def.libelle} description="L'acte est inscrit au journal des états du budget et à l'historique." onFermer={onFermer} actions={actionsDialogue(onFermer, 'Confirmer', action.enCours, () => action.lancer(undefined))}>
      {code === 'adopter' ? <Champ libelle="Référence de la délibération" value={v.deliberation_reference} onChange={(e) => setV((x) => ({ ...x, deliberation_reference: e.target.value }))} erreur={champ('deliberation_reference')} required /> : null}
      {code === 'adopter' || code === 'rendre_executoire' || code === 'clore' ? (
        <Champ libelle={code === 'adopter' ? 'Date de la délibération' : code === 'rendre_executoire' ? 'Date de transmission au représentant de l\'État' : 'Date de clôture'} type="date" value={v.date} onChange={(e) => setV((x) => ({ ...x, date: e.target.value }))} erreur={champ('date')} required />
      ) : null}
      <ZoneTexte libelle={code === 'renvoyer' ? 'Motif du renvoi' : 'Observation (facultative)'} value={v.motif} onChange={(e) => setV((x) => ({ ...x, motif: e.target.value }))} erreur={champ('motif')} required={code === 'renvoyer'} />
      {action.messageErreur ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}

export function FormulaireDecisionModificative({ budgetId, lignes, fictif, onFermer }: { budgetId: string; lignes: LigneBudgetaire[]; fictif: boolean; onFermer: () => void }) {
  const [v, setV] = useState({ reference: '', date: aujourdhuiIso(), deliberation_reference: '', motif: '' });
  const [mouvements, setMouvements] = useState<MouvementSaisi[]>([{ ligne_id: '', montant: '' }]);
  const [erreursLocales, setErreursLocales] = useState<string[]>([]);
  const controle = controlerMouvements(mouvements);
  const action = useAction({
    executer: () => {
      if (controle.erreurs.length > 0) {
        setErreursLocales(controle.erreurs);
        return Promise.reject(new Error('Formulaire incomplet'));
      }
      setErreursLocales([]);
      return deciderModification(budgetId, { reference: v.reference, date: v.date, deliberation_reference: v.deliberation_reference || undefined, motif: v.motif, mouvements: controle.mouvements });
    },
    succes: (d) => `Décision ${d.reference} enregistrée : ${d.mouvements.length} mouvement(s).`,
    invalider: INVALIDER_BUDGET,
    apres: onFermer,
  });
  const champ = (nom: string) => action.erreursChamps.find((e) => e.champ === nom)?.message;
  const options = lignes.map((l) => ({ valeur: l.id, libelle: `${l.code} · ${l.libelle} (disponible ${francs(l.disponible, fictif)})` }));
  return (
    <Dialogue ouvert titre="Décision modificative" description="Chaque mouvement ajoute ou retire des crédits sur une ligne ; rien n'est réécrit, tout est tracé." onFermer={onFermer} actions={actionsDialogue(onFermer, 'Enregistrer la décision', action.enCours, () => action.lancer(undefined))}>
      <div className={styles.grille}>
        <Champ libelle="Référence de la décision" value={v.reference} onChange={(e) => setV((x) => ({ ...x, reference: e.target.value }))} erreur={champ('reference')} required />
        <Champ libelle="Date" type="date" value={v.date} onChange={(e) => setV((x) => ({ ...x, date: e.target.value }))} erreur={champ('date')} required />
        <Champ libelle="Délibération (facultative)" value={v.deliberation_reference} onChange={(e) => setV((x) => ({ ...x, deliberation_reference: e.target.value }))} />
      </div>
      <ZoneTexte libelle="Motif" value={v.motif} onChange={(e) => setV((x) => ({ ...x, motif: e.target.value }))} erreur={champ('motif')} required />
      <fieldset className={styles.mouvements}>
        <legend>Mouvements de crédits</legend>
        {mouvements.map((m, i) => (
          <div key={i} className={styles.mouvement}>
            <Selection libelle={`Ligne du mouvement ${i + 1}`} options={options} optionVide="Choisir une ligne" value={m.ligne_id} onChange={(e) => setMouvements((x) => x.map((y, j) => (j === i ? { ...y, ligne_id: e.target.value } : y)))} />
            <Champ libelle={`Montant du mouvement ${i + 1} (F)`} type="number" value={m.montant} onChange={(e) => setMouvements((x) => x.map((y, j) => (j === i ? { ...y, montant: e.target.value } : y)))} />
            {mouvements.length > 1 ? <Bouton variante="discret" icone={Trash2} onClick={() => setMouvements((x) => x.filter((_, j) => j !== i))}>Retirer</Bouton> : null}
          </div>
        ))}
        <Bouton variante="secondaire" icone={Plus} onClick={() => setMouvements((x) => [...x, { ligne_id: '', montant: '' }])}>Ajouter un mouvement</Bouton>
        <p className={styles.note}>Total de la décision : {francs(controle.total, fictif)}</p>
      </fieldset>
      {erreursLocales.length > 0 ? <Bandeau type="erreur">{erreursLocales.join(' ; ')}</Bandeau> : null}
      {action.messageErreur && action.messageErreur !== 'Formulaire incomplet' ? <Bandeau type="erreur">{action.messageErreur}</Bandeau> : null}
    </Dialogue>
  );
}
